import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  CONSTRUCTION_CATEGORIES,
  useConstructionFields,
  useConstructionSettings,
  useConstructionTypes,
  useDeleteConstructionSetting,
  useDeleteConstructionType,
  ConstructionType,
  ConstructionSetting,
  ConstructionFieldSpec,
} from "../../api/constructionTypes";
import { useProject, useUpdateProject } from "../../api/project";
import { ConstructionSettingEditor } from "./ConstructionSettingEditor";
import { ConstructionTypeEditor } from "./ConstructionTypeEditor";
import { C, modifierSummary, primaryBtnSx, inputSx } from "./constructionUi";

type View = "types" | "characteristics";

export default function ConstructionTypesTab() {
  const { project } = useProject();
  const projectData = project?.data;
  const updateProject = useUpdateProject(projectData?.id as string);

  const fieldsQ = useConstructionFields();
  const typesQ = useConstructionTypes();
  const settingsQ = useConstructionSettings();
  const deleteType = useDeleteConstructionType();
  const deleteSetting = useDeleteConstructionSetting();

  const fields: ConstructionFieldSpec[] = fieldsQ.data?.data?.fields ?? [];
  const types = typesQ.data?.data ?? [];
  const settings = settingsQ.data?.data ?? [];

  const fieldByName = useMemo(
    () => new Map(fields.map((f) => [f.name, f])),
    [fields]
  );

  const [view, setView] = useState<View>("types");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const flash = (message: string, severity: "success" | "error" = "success") =>
    setSnackbar({ open: true, message, severity });

  const [editingType, setEditingType] = useState<ConstructionType | null>(null);
  const [creatingType, setCreatingType] = useState(false);
  const [editingSetting, setEditingSetting] = useState<ConstructionSetting | null>(null);
  const [creatingSetting, setCreatingSetting] = useState(false);

  const defaultTypeId = projectData?.default_construction_type ?? "";

  const handleSaveDefault = async (value: number | "") => {
    try {
      await updateProject.mutateAsync({
        defaultConstructionTypeId: value === "" ? null : value,
      } as any);
      flash("Default construction type updated");
    } catch (e: any) {
      flash(e?.message || "Failed to update default", "error");
    }
  };

  const handleDeleteType = async (t: ConstructionType) => {
    if (!confirm(`Delete construction type “${t.name}”?`)) return;
    try {
      await deleteType.mutateAsync(t.id);
      flash("Construction type deleted");
    } catch (e: any) {
      flash(e?.message || "Failed to delete", "error");
    }
  };

  const handleDeleteSetting = async (s: ConstructionSetting) => {
    if (!confirm(`Delete characteristic “${s.name}”?`)) return;
    try {
      await deleteSetting.mutateAsync(s.id);
      flash("Characteristic deleted");
    } catch (e: any) {
      flash(e?.message || "Failed to delete", "error");
    }
  };

  const loading = fieldsQ.isLoading || typesQ.isLoading || settingsQ.isLoading;

  return (
    <Box sx={{ maxWidth: 920, mx: "auto" }}>
      {/* Intro */}
      <Typography variant="h6" sx={{ color: C.text, fontWeight: 600 }}>
        Construction
      </Typography>
      <Typography variant="body2" sx={{ color: C.dim, mb: 3, maxWidth: 680 }}>
        Define how buildings respond to fire. <strong>Structure characteristics</strong> are
        individual features — closed eaves, sprinklers, a roof material — that each nudge the
        fire-spread physics. Bundle them into a <strong>construction type</strong> and assign
        it to your building assets.
      </Typography>

      {/* Default type */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${C.border}`,
          backgroundColor: C.panel,
        }}
      >
        <HomeWorkOutlinedIcon sx={{ color: C.accent }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: C.text, fontWeight: 600 }}>
            Default for new buildings
          </Typography>
          <Typography variant="caption" sx={{ color: C.faint }}>
            Applied to building assets that don't have their own type set.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ ...inputSx, minWidth: 240 }}>
          <Select
            value={defaultTypeId}
            displayEmpty
            onChange={(e) =>
              handleSaveDefault(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            <MenuItem value="">
              <em>None (simulator default)</em>
            </MenuItem>
            {types.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Segmented toggle + create button */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, v) => v && setView(v)}
          sx={{
            backgroundColor: "rgba(255,255,255,0.04)",
            borderRadius: 2,
            p: 0.5,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "8px !important",
              textTransform: "none",
              fontWeight: 600,
              color: C.dim,
              px: 2,
              "&.Mui-selected": {
                backgroundColor: C.accent,
                color: "#003b30",
                "&:hover": { backgroundColor: C.accentHover },
              },
              "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
            },
          }}
        >
          <ToggleButton value="types">
            Types{types.length ? ` · ${types.length}` : ""}
          </ToggleButton>
          <ToggleButton value="characteristics">
            Characteristics{settings.length ? ` · ${settings.length}` : ""}
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            view === "types" ? setCreatingType(true) : setCreatingSetting(true)
          }
          sx={primaryBtnSx}
        >
          {view === "types" ? "New type" : "New characteristic"}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: C.accent }} />
        </Box>
      ) : view === "types" ? (
        <TypesView
          types={types}
          onEdit={setEditingType}
          onDelete={handleDeleteType}
          onCreate={() => setCreatingType(true)}
        />
      ) : (
        <CharacteristicsView
          settings={settings}
          fieldByName={fieldByName}
          onEdit={setEditingSetting}
          onDelete={handleDeleteSetting}
          onCreate={() => setCreatingSetting(true)}
        />
      )}

      {/* Editors */}
      {(creatingType || editingType) && (
        <ConstructionTypeEditor
          open
          type={editingType}
          settings={settings}
          fields={fields}
          onClose={(saved) => {
            setCreatingType(false);
            setEditingType(null);
            if (saved) flash("Construction type saved");
          }}
        />
      )}
      {(creatingSetting || editingSetting) && (
        <ConstructionSettingEditor
          open
          setting={editingSetting}
          fields={fields}
          onClose={(saved) => {
            setCreatingSetting(false);
            setEditingSetting(null);
            if (saved) flash("Characteristic saved");
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            backgroundColor: snackbar.severity === "success" ? C.accent : "#ef5350",
            color: "#000",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Types view
// ---------------------------------------------------------------------------

function TypesView({
  types,
  onEdit,
  onDelete,
  onCreate,
}: {
  types: ConstructionType[];
  onEdit: (t: ConstructionType) => void;
  onDelete: (t: ConstructionType) => void;
  onCreate: () => void;
}) {
  if (types.length === 0) {
    return (
      <EmptyState
        icon={<HomeWorkOutlinedIcon sx={{ fontSize: 40, color: C.faint }} />}
        title="No construction types yet"
        body="Create a type to bundle characteristics together and assign it to buildings."
        actionLabel="New type"
        onAction={onCreate}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 1.5,
      }}
    >
      {types.map((t) => (
        <Box
          key={t.id}
          onClick={() => !t.is_system_default && onEdit(t)}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${C.border}`,
            backgroundColor: C.panel,
            transition: "background-color 120ms, border-color 120ms",
            cursor: t.is_system_default ? "default" : "pointer",
            "&:hover": t.is_system_default
              ? {}
              : { backgroundColor: C.panelHover, borderColor: "rgba(0,212,173,0.4)" },
            "&:hover .row-actions": { opacity: 1 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: C.text, fontWeight: 600, flex: 1 }}>{t.name}</Typography>
            {t.is_system_default ? (
              <Tooltip title="Built-in — read only">
                <Chip
                  icon={<LockOutlinedIcon sx={{ fontSize: 13, color: `${C.faint} !important` }} />}
                  label="Built-in"
                  size="small"
                  sx={{
                    height: 20,
                    color: C.faint,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem" },
                  }}
                />
              </Tooltip>
            ) : (
              <Box className="row-actions" sx={{ opacity: 0, transition: "opacity 120ms" }}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(t); }} sx={{ color: C.accent }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(t); }} sx={{ color: C.danger }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {t.description && (
            <Typography variant="caption" sx={{ color: C.faint, display: "block", mt: 0.25 }}>
              {t.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
            {t.settings.length === 0 ? (
              <Typography variant="caption" sx={{ color: C.faint, fontStyle: "italic" }}>
                No characteristics — simulator baseline only.
              </Typography>
            ) : (
              t.settings.map((s) => (
                <Chip
                  key={s.id}
                  label={s.name}
                  size="small"
                  sx={{
                    height: 22,
                    backgroundColor: C.accentSoft,
                    color: C.accent,
                    "& .MuiChip-label": { px: 1, fontSize: "0.72rem" },
                  }}
                />
              ))
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Characteristics view (grouped by category)
// ---------------------------------------------------------------------------

function CharacteristicsView({
  settings,
  fieldByName,
  onEdit,
  onDelete,
  onCreate,
}: {
  settings: ConstructionSetting[];
  fieldByName: Map<string, ConstructionFieldSpec>;
  onEdit: (s: ConstructionSetting) => void;
  onDelete: (s: ConstructionSetting) => void;
  onCreate: () => void;
}) {
  if (settings.length === 0) {
    return (
      <EmptyState
        icon={<TuneIcon sx={{ fontSize: 40, color: C.faint }} />}
        title="No characteristics yet"
        body="Characteristics are the building blocks of construction types."
        actionLabel="New characteristic"
        onAction={onCreate}
      />
    );
  }

  return (
    <Stack spacing={3}>
      {CONSTRUCTION_CATEGORIES.map((cat) => {
        const inCat = settings.filter((s) => s.category === cat.value);
        if (inCat.length === 0) return null;
        return (
          <Box key={cat.value}>
            <Typography
              sx={{
                color: C.faint,
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              {cat.label}
            </Typography>
            <Stack spacing={1}>
              {inCat.map((s) => (
                <Box
                  key={s.id}
                  onClick={() => !s.is_system_default && onEdit(s)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${C.border}`,
                    backgroundColor: C.panel,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    cursor: s.is_system_default ? "default" : "pointer",
                    transition: "background-color 120ms, border-color 120ms",
                    "&:hover": s.is_system_default
                      ? {}
                      : { backgroundColor: C.panelHover, borderColor: "rgba(0,212,173,0.4)" },
                    "&:hover .row-actions": { opacity: 1 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography sx={{ color: C.text, fontWeight: 500 }}>{s.name}</Typography>
                      {s.is_system_default && (
                        <Tooltip title="Built-in — read only">
                          <LockOutlinedIcon sx={{ fontSize: 13, color: C.faint }} />
                        </Tooltip>
                      )}
                    </Box>
                    {s.description && (
                      <Typography variant="caption" sx={{ color: C.faint, display: "block" }}>
                        {s.description}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                      {s.modifiers.map((m, i) => (
                        <Chip
                          key={i}
                          label={modifierSummary(m, fieldByName)}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            color: C.dim,
                            borderColor: C.border,
                            "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem" },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  {!s.is_system_default && (
                    <Box className="row-actions" sx={{ opacity: 0, transition: "opacity 120ms", display: "flex" }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(s); }} sx={{ color: C.accent }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(s); }} sx={{ color: C.danger }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({
  icon,
  title,
  body,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Box
      sx={{
        textAlign: "center",
        py: 6,
        px: 3,
        borderRadius: 2,
        border: `1px dashed ${C.border}`,
        backgroundColor: C.panel,
      }}
    >
      {icon}
      <Typography sx={{ color: C.text, fontWeight: 600, mt: 1 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: C.dim, mt: 0.5, mb: 2.5, maxWidth: 420, mx: "auto" }}>
        {body}
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onAction} sx={primaryBtnSx}>
        {actionLabel}
      </Button>
    </Box>
  );
}
